import React, { useState, useEffect, useCallback } from 'react';
import { LinkItem } from '../../types/Links';
import './LinkCard.css';

interface LinkCardProps {
  link: LinkItem;
  categoryColor: string;
}

/// 各リンクカードコンポーネント - 高画質サイトアイコン背景付き
const LinkCard: React.FC<LinkCardProps> = ({ link, categoryColor }) => {
  const [backgroundImage, setBackgroundImage] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  /// ローカル画像パスを取得する関数（フォールバック対応）
  const getLocalImagePath = useCallback((linkId: string): string => {
    // URLドメインに基づくフォールバック対応
    const url = link.url.toLowerCase();
    
    // YouTube系チャンネルはyoutube.pngを使用
    if (url.includes('youtube.com/')) return '/assets/images/links/youtube.png';
    // X(旧Twitter)はx.pngを使用
    if (url.includes('twitter.com/') || url.includes('x.com/')) return '/assets/images/links/x.png';
    // TwitCastingはtwitcasting.pngを使用
    if (url.includes('twitcasting.tv/')) return '/assets/images/links/twitcasting.png';
    // PIXIV FANBOXはpixiv_fanbox.pngを使用
    if (url.includes('fanbox.cc/')) return '/assets/images/links/pixiv_fanbox.png';
    // PIXIVはpixiv.pngを使用
    if (url.includes('pixiv.net/')) return '/assets/images/links/pixiv.png';
    // BOOTHはbooth.pngを使用
    if (url.includes('booth.pm/')) return '/assets/images/links/booth.png';
    // MelonBooksはmelonbooks.pngを使用
    if (url.includes('melonbooks.co.jp/')) return '/assets/images/links/melonbooks.png';
    // quizmakerはquizmaker.pngを使用
    if (url.includes('quiz-maker.site/')) return '/assets/images/links/quizmaker.png';

    
    // その他は元のIDを使用
    return `/assets/images/links/${linkId}.png`;
  }, [link.url]);

  /// ローカル画像の読み込み処理
  useEffect(() => {
    const loadLocalImage = () => {
      const localImagePath = getLocalImagePath(link.id);
      
      const testImage = new Image();
      testImage.onload = () => {
        setBackgroundImage(localImagePath);
        setImageLoaded(true);
      };
      testImage.onerror = () => {
        // ローカル画像が見つからない場合は背景画像なしで表示
        setImageLoaded(false);
      };
      testImage.src = localImagePath;
    };

    loadLocalImage();
  }, [link.id, getLocalImagePath]);

  /// 外部リンクかどうかの判定
  const isExternalLink = (url: string): boolean => {
    return url.startsWith('http://') || url.startsWith('https://');
  };

  /// リンククリック処理
  const handleClick = () => {
    if (isExternalLink(link.url)) {
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = link.url;
    }
  };

  /// キーボードイベント処理
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={`link-card ${imageLoaded ? 'has-background-image' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`${link.name}を開く: ${link.description}`}
      data-external={isExternalLink(link.url)}
      style={{
        '--card-bg-image': backgroundImage ? `url(${backgroundImage})` : 'none',
        '--accent-color': categoryColor
      } as React.CSSProperties & { '--card-bg-image': string; '--accent-color': string }}
    >
      <div 
        className="link-card-accent" 
        style={{ backgroundColor: categoryColor }}
      />
      
      <div className="link-card-background-overlay" />
      
      <div className="link-card-content">
        <div className="link-card-info">
          <h3 className="link-card-title">{link.name}</h3>
          <p className="link-card-description">{link.description}</p>
        </div>
      </div>
    </div>
  );
};

export default LinkCard; 